// ──────────────────────────────────────────────
// BaseService — abstract base for pipeline services
// Provides subscribe/getSnapshot for useSyncExternalStore
// and a typed event emitter.
// ──────────────────────────────────────────────

import SentryService from '@services/SentryService';

/**
 * Abstract base class for recording pipeline services.
 *
 * Subclasses define their own TState and TEventMap, then call
 * `setState()` and `emit()` to drive React UI updates and
 * cross-service communication.
 *
 * React integration:
 *   const state = useSyncExternalStore(
 *     (cb) => service.subscribe(cb),
 *     () => service.getSnapshot(),
 *   );
 */
export abstract class BaseService<
  TState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TEventMap extends Record<string, any> = Record<string, never>,
> {
  private subscribers = new Set<() => void>();
  private listeners = new Map<keyof TEventMap, Set<(data: any) => void>>();
  protected state: TState;

  constructor(initialState: TState) {
    this.state = initialState;
  }

  // ── React integration (useSyncExternalStore) ──

  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  getSnapshot(): TState {
    return this.state;
  }

  // ── Events ──

  on<K extends keyof TEventMap>(event: K, handler: (data: TEventMap[K]) => void): void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler);
  }

  off<K extends keyof TEventMap>(event: K, handler: (data: TEventMap[K]) => void): void {
    this.listeners.get(event)?.delete(handler);
  }

  // ── Protected helpers for subclasses ──

  protected setState(next: TState): void {
    this.state = next;
    this.notifySubscribers();
  }

  protected emit<K extends keyof TEventMap>(event: K, data: TEventMap[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const handler of set) {
      try {
        handler(data);
      } catch (error) {
        SentryService.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    }
  }

  protected notifySubscribers(): void {
    for (const cb of this.subscribers) {
      cb();
    }
  }

  /** Subclasses should override with cleanup logic and call super.dispose(). */
  dispose(): void {
    this.subscribers.clear();
    this.listeners.clear();
  }
}
